/**
 * Users Tab Component
 * Displays list of users with search, filter, and CRUD actions
 */
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { 
  Search, 
  UserPlus, 
  Mail, 
  Building2, 
  UserCog, 
  Edit, 
  Trash2 
} from "lucide-react";

export function UsersTab({ 
  users, 
  searchQuery, 
  setSearchQuery, 
  roleFilter, 
  setRoleFilter,
  onCreateUser,
  onEditUser,
  onDeleteUser,
  onImpersonate,
  currentUserId,
  impersonating,
  getRoleBadgeColor
}) {
  // Helper to get full name
  const getFullName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.email;
  };
  
  // Filter users based on search and role
  const filteredUsers = users.filter(u => {
    const fullName = getFullName(u).toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.organization?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-slate-200"
            data-testid="user-search-input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200"
          data-testid="user-role-filter"
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="university">University</option>
          <option value="clinical_site">Clinical Site</option>
        </select>
        <Button 
          onClick={onCreateUser}
          className="bg-cyan-400 text-black hover:bg-cyan-300"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.map((u) => {
          const fullName = getFullName(u);
          const initials = u.first_name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?';
          return (
          <Card key={u.id} className="bg-[#151A23] border-slate-700 hover:border-slate-600 transition-colors">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-semibold text-slate-300">
                      {initials}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-white">{fullName}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                        {u.role.replace("_", " ")}
                      </span>
                      {u.role === "clinical_site" && u.access_level && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.access_level === "organization" ? "bg-purple-500/20 text-purple-400" : "bg-orange-500/20 text-orange-400"}`}>
                          {u.access_level === "organization" ? "All Sites" : "Specific Sites"}
                        </span>
                      )}
                      {!u.is_active && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {u.email}
                      </span>
                      {u.organization && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {u.organization}
                        </span>
                      )}
                      {u.clinical_site_name && (
                        <span className="text-violet-400">{u.clinical_site_name}</span>
                      )}
                      {u.managed_site_names && u.managed_site_names.length > 0 && (
                        <span className="text-orange-400 text-xs">
                          ({u.managed_site_names.length} site{u.managed_site_names.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {u.role !== "admin" && u.id !== currentUserId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onImpersonate(u)}
                      disabled={impersonating}
                      className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-400/10"
                      data-testid={`impersonate-user-${u.id}`}
                      title="Act as this user"
                    >
                      <UserCog className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditUser({...u, managed_site_ids: u.managed_site_ids || []})}
                    className="text-slate-400 hover:text-white hover:bg-white/5"
                    data-testid={`edit-user-${u.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {u.id !== currentUserId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser(u.id)}
                      className="text-slate-400 hover:text-red-400 hover:bg-red-400/10"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )})}
      </div>
    </div>
  );
}
